import { applyPct } from "./arith";
import { DomainError } from "./errors";
import { MAX_PRICE_CENTS } from "./pricing";
import { MAX_STOCK_QTY_Q4 } from "./stock";

// ===== Propostas comerciais (RF-VEN-01; 05 §2 e §3) =====

/** Itens por proposta (cadastrados + outros): a tela e o PDF mostram todos. */
export const MAX_QUOTE_ITEMS = 200;
/** Teto de total de documento (R$ 1 trilhão): bem abaixo de 2^53 centavos. */
export const MAX_DOCUMENT_CENTS = 100_000_000_000_000;
/** Desconto de 0% a 100% (pct4). */
export const MAX_ITEM_DISCOUNT_PCT4 = 1_000_000;

/** Modalidade do frete (NF-e modFrete): 0 CIF, 1 FOB, 2 terceiros, 3 próprio remetente, 4 próprio destinatário, 9 sem. */
export const FREIGHT_MODES = [0, 1, 2, 3, 4, 9] as const;
export type FreightMode = (typeof FREIGHT_MODES)[number];
export const FREIGHT_MODE_LABELS: Readonly<Record<FreightMode, string>> = {
  0: "Por conta do remetente (CIF)",
  1: "Por conta do destinatário (FOB)",
  2: "Por conta de terceiros",
  3: "Transporte próprio do remetente",
  4: "Transporte próprio do destinatário",
  9: "Sem frete",
};

export const COMMERCIAL_TERMS_MODES = ["none", "installments", "text"] as const;
export type CommercialTermsMode = (typeof COMMERCIAL_TERMS_MODES)[number];
export const COMMERCIAL_TERMS_LABELS: Readonly<Record<CommercialTermsMode, string>> = {
  none: "Nenhuma",
  installments: "Parcelas",
  text: "Texto",
};

const fail = (code: string, message: string): never => {
  throw new DomainError(code, message);
};
const isInt = Number.isSafeInteger;

/** Divisão com arredondamento comercial (meio para cima), só para valores não negativos. */
const roundDiv = (num: bigint, den: bigint) => (2n * num + den) / (2n * den);

export type QuoteLineInput = { listPriceCents: number; discountPct4: number; qtyQ4: number };
export type QuoteLine = { unitPriceCents: number; totalCents: number; discountCents: number };

/** Linha (05 §2): `unit = list − applyPct(list, desc)`, `total = unit × qtd`, desconto = bruto − total. BigInt. */
export function quoteLine(i: QuoteLineInput): QuoteLine {
  if (!isInt(i.qtyQ4) || i.qtyQ4 <= 0 || i.qtyQ4 > MAX_STOCK_QTY_Q4)
    fail("invalid_qty", "Quantidade do item deve ser positiva e dentro do limite");
  if (!isInt(i.listPriceCents) || i.listPriceCents < 0 || i.listPriceCents > MAX_PRICE_CENTS)
    fail("invalid_money", "Preço do item fora do limite");
  if (!isInt(i.discountPct4) || i.discountPct4 < 0 || i.discountPct4 > MAX_ITEM_DISCOUNT_PCT4)
    fail("invalid_discount", "O desconto do item vai de 0% a 100%");
  const unit = i.listPriceCents - applyPct(i.listPriceCents, i.discountPct4);
  const qty = BigInt(i.qtyQ4);
  const total = roundDiv(BigInt(unit) * qty, 10_000n);
  const gross = roundDiv(BigInt(i.listPriceCents) * qty, 10_000n);
  if (gross > BigInt(MAX_DOCUMENT_CENTS)) fail("total_limit", "Total do item acima do limite");
  return { unitPriceCents: unit, totalCents: Number(total), discountCents: Number(gross - total) };
}

export type QuoteCalcItem = QuoteLineInput & { kind: "product" | "other" };
export type QuoteCalcInput = {
  items: readonly QuoteCalcItem[];
  /** Desconto geral em valor (usado quando `discountPct4` é null). */
  discountCents: number;
  /** Desconto geral em % sobre itens + outros itens; null = em valor. */
  discountPct4: number | null;
  freightCents: number;
  otherExpensesCents: number;
};
export type QuoteTotals = {
  lines: QuoteLine[];
  itemsTotalCents: number;
  otherItemsTotalCents: number;
  /** Soma dos descontos dos itens (bruto − líquido). */
  itemsDiscountCents: number;
  /** Desconto geral efetivo em centavos. */
  discountCents: number;
  freightCents: number;
  otherExpensesCents: number;
  totalCents: number;
  itemsCount: number;
  quantitySumQ4: number;
};

const money = (v: number, what: string) => {
  if (!isInt(v) || v < 0 || v > MAX_PRICE_CENTS) fail("invalid_money", `${what} fora do limite`);
  return v;
};

/**
 * Totais da proposta (RF-VEN-01; 05 §3): itens, outros itens, desconto geral (valor ou % sobre o subtotal, nunca
 * acima dele), frete e outras despesas. Somas em BigInt; bruto dos itens (líquido + descontos dos itens) acima de
 * MAX_DOCUMENT_CENTS = `total_limit`.
 */
export function quoteTotals(i: QuoteCalcInput): QuoteTotals {
  if (i.items.length > MAX_QUOTE_ITEMS) fail("too_many_items", `No máximo ${MAX_QUOTE_ITEMS} itens`);
  const freight = money(i.freightCents, "Frete");
  const other = money(i.otherExpensesCents, "Outras despesas");
  const lines = i.items.map(quoteLine);
  let items = 0n;
  let others = 0n;
  let itemsDiscount = 0n;
  let qty = 0n;
  i.items.forEach((it, k) => {
    const l = lines[k]!;
    if (it.kind === "product") items += BigInt(l.totalCents);
    else others += BigInt(l.totalCents);
    itemsDiscount += BigInt(l.discountCents);
    qty += BigInt(it.qtyQ4);
  });
  const subtotal = items + others;
  // Teto sobre o bruto (líquido + desconto dos itens ≥ líquido): cobre também a soma dos descontos dos itens, que com
  // o líquido zerado passaria do teto (e de 2^53) sem erro.
  if (subtotal + itemsDiscount > BigInt(MAX_DOCUMENT_CENTS))
    fail("total_limit", "Total da proposta acima do limite");
  let discount: number;
  if (i.discountPct4 !== null) {
    if (!isInt(i.discountPct4) || i.discountPct4 < 0 || i.discountPct4 > MAX_ITEM_DISCOUNT_PCT4)
      fail("invalid_discount", "O desconto geral vai de 0% a 100%");
    discount = applyPct(Number(subtotal), i.discountPct4);
  } else {
    if (!isInt(i.discountCents) || i.discountCents < 0) fail("invalid_discount", "Desconto geral inválido");
    if (BigInt(i.discountCents) > subtotal)
      fail("invalid_discount", "O desconto geral não pode passar do total dos itens");
    discount = i.discountCents;
  }
  return {
    lines,
    itemsTotalCents: Number(items),
    otherItemsTotalCents: Number(others),
    itemsDiscountCents: Number(itemsDiscount),
    discountCents: discount,
    freightCents: freight,
    otherExpensesCents: other,
    totalCents: Number(subtotal) - discount + freight + other,
    itemsCount: i.items.length,
    quantitySumQ4: Number(qty),
  };
}

// ---------- conversão em pedido (contrato para a T1.8) ----------

export type QuoteDraftItem = {
  productId: string | null;
  description: string;
  code: string | null;
  unit: string | null;
  qtyQ4: number;
  listPriceCents: number;
  discountPct4: number;
  unitPriceCents: number;
  totalCents: number;
};
export type QuoteForDraft = {
  id: string;
  number: number;
  contactId: string;
  sellerId: string | null;
  priceListId: string | null;
  paymentTermId: string | null;
  carrierId: string | null;
  freightMode: FreightMode;
  discountPct4: number | null;
  discountCents: number;
  freightCents: number;
  otherExpensesCents: number;
  generalTerms: string | null;
  items: readonly QuoteDraftItem[];
};

/** Rascunho do pedido de venda que a T1.8 grava (origem "quote", 03-schema `sales_orders.origin`). */
export type SalesOrderDraft = Omit<QuoteForDraft, "id" | "number" | "items" | "generalTerms"> & {
  origin: "quote";
  quoteId: string;
  quoteNumber: number;
  /** Data do pedido (AAAA-MM-DD, hoje em São Paulo na conversão). */
  date: string;
  notes: string;
  items: (QuoteDraftItem & { position: number })[];
};

/** Proposta → rascunho do pedido: mesmos itens (na ordem), valores, cliente, vendedor, lista, condição e frete. */
export function quoteToOrderDraft(q: QuoteForDraft, date: string): SalesOrderDraft {
  const { id, number, items, generalTerms, ...rest } = q;
  return {
    ...rest,
    origin: "quote",
    quoteId: id,
    quoteNumber: number,
    date,
    notes: [`Proposta comercial nº ${number}`, generalTerms].filter(Boolean).join("\n"),
    items: items.map((it, k) => ({ ...it, position: k + 1 })),
  };
}
